<template>
  <PageLayout>
    <template v-slot:heading>{{ $t("profile.heading") }}</template>

    <template v-slot:action-buttons>
      <div v-if="isDirty">
        <ActionButton :on-click="reset">
          {{ $t("profile.cancel") }}
        </ActionButton>
        <ActionButton
          :loading="saving"
          :on-click="saveProfile"
          button-type="primary"
          :left-pad="true"
          :disabled="!isValid"
        >
          {{ $t("profile.save") }}
        </ActionButton>
      </div>
    </template>

    <PageSection>
      <template v-slot:heading>{{ $t("personal-details.heading") }}</template>
      <template v-slot:description>{{ $t("personal-details.description") }}</template>

      <!-- <div class="fpds-CardHeader">
        <img :src="picture" class="profilePicture" />
        <ActionButton :on-click="uploadPhoto">
          {{ $t("personal-details.remove-photo") }}
        </ActionButton>
        <ActionButton :on-click="uploadPhoto" button-type="primary">
          {{ $t("personal-details.upload-photo") }}
        </ActionButton>
      </div> -->

      <div class="fpds-Alert u-marginBottomMedium" v-if="isFPHUser">
        <i class="fpds-Alert-icon fpds-Icon fpds-Icon--medium">info</i>
        <div class="fpds-Alert-content">
          <div class="fpds-Alert-body">
            {{ $t("personal-details.personal-details-update-disabled") }}
          </div>
        </div>
      </div>

      <form class="fpds-Form" autocomplete="off">
        <InputBox
          id="firstName"
          v-model="profile.firstName"
          :placeholder="$t('personal-details.first-name')"
          :is-valid="firstNameIsValid"
          :caption="firstNameInvalidMessage"
          :disabled="isFPHUser"
        >
          {{ $t("personal-details.first-name") }}
        </InputBox>
        <InputBox
          id="lastName"
          v-model="profile.lastName"
          :placeholder="$t('personal-details.last-name')"
          :is-valid="lastNameIsValid"
          :caption="lastNameInvalidMessage"
          :disabled="isFPHUser"
        >
          {{ $t("personal-details.last-name") }}
        </InputBox>

        <div class="fpds-Form-item">
          <label class="fpds-Form-itemLabel fpds-FieldLabel">{{
            $t("personal-details.email-address")
          }}</label>
          <div class="u-flex">
            <div class="u-flexInitial">
              <div class="fpds-Form-itemControl">{{ profile.email }}</div>
            </div>
            <div class="u-flexFill">
            </div>
          </div>
        </div>

        <CountrySelector v-model="profile.country" :disabled="isFPHUser" />
      </form>
    </PageSection>
     
    <div v-if="!isFPHUser">
      <hr class="fpds-Divider" />
      <SecuritySection />
    </div>
  </PageLayout>
</template>

<script>
import _ from "lodash";
import { mapState } from "vuex";

import PageLayout from "../components/PageLayout";
import PageSection from "../components/PageSection";
import InputBox from "../components/InputBox";
import CountrySelector from "../components/CountrySelector";
import SecuritySection from "../components/SecuritySection";
import ActionButton from "../components/ActionButton";
import ProfileService from "../services/ProfileService";
import { fphEmailDomain } from "@/constants/index.js";

import "../styles/main.scss";

const nameHasNoSpecialCharacters = (value) => /^[\p{L}\s]+$/u.test(value);

export default {
  components: { PageLayout, PageSection, InputBox, CountrySelector, SecuritySection, ActionButton },
  data() {
    return {
      saving: false,
      profile: {
        firstName: "",
        lastName: "",
        picture: "",
        country: "",
        email: "",
      },
    };
  },
  computed: {
    ...mapState({
      currentProfile: (state) => state.currentProfile,
    }),
    isDirty() {
      return (
        this.currentProfile?.firstName !== this.profile?.firstName ||
        this.currentProfile?.lastName !== this.profile?.lastName ||
        this.currentProfile?.picture !== this.profile?.picture ||
        this.currentProfile?.country !== this.profile?.country ||
        this.currentProfile?.email !== this.profile?.email
      );
    },
     isFPHUser() {
      return Boolean(this.currentProfile.email && this.currentProfile.email.includes(fphEmailDomain) && !this.currentProfile.email.includes("+"));
    },
    isValid() {
      return this.firstNameIsValid && this.lastNameIsValid;
    },
    firstNameIsDirty() {
      return this.currentProfile?.firstName !== this.profile?.firstName;
    },
    lastNameIsDirty() {
      return this.currentProfile?.lastName !== this.profile?.lastName;
    },
    firstNameIsValid() {
      return !this.firstNameIsDirty || !this.firstNameInvalidMessage?.length;
    },
    lastNameIsValid() {
      return !this.lastNameIsDirty || !this.lastNameInvalidMessage?.length;
    },
    firstNameInvalidMessage() {
      if (!this.profile.firstName || this.profile.firstName.length === 0) {
        return this.$t("profile.errors.firstname-must-be-set");
      }

      if (!nameHasNoSpecialCharacters(this.profile.firstName)) {
        return this.$t("profile.errors.firstname-must-not-contain-special-characters");
      }

      if (this.profile.firstName.length > 150) {
        return this.$t("profile.errors.firstname-must-be-less-than-150");
      }

      return undefined;
    },
    lastNameInvalidMessage() {
      if (!this.profile.lastName || this.profile.lastName.length === 0) {
        return this.$t("profile.errors.lastname-must-be-set");
      }

      if (!nameHasNoSpecialCharacters(this.profile.lastName)) {
        return this.$t("profile.errors.lastname-must-not-contain-special-characters");
      }

      if (this.profile.lastName.length > 150) {
        return this.$t("profile.errors.lastname-must-be-less-than-150");
      }

      return undefined;
    },
  },
  async mounted() {
    this.reset();
    console.log("currentProfile on load:", this.currentProfile);
  },
  methods: {
    async uploadPhoto() {
      alert("Not Implemented: Upload photo");
    },
    async reset() {
      const profile = this.currentProfile;
      _.merge(this.profile, profile);
    },
    async saveProfile() {
      if (!this.isValid) {
        return;
      }
      this.saving = true;
      try {
        const profile = await ProfileService.saveProfile(this.profile);
        this.$store.commit("setCurrentProfile", profile);
        _.merge(this.profile, profile);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
